import React, { Suspense } from 'react';
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { FillPage } from '../../layout/FillPage';
import {
  Button,
  Callout,
  EmptyState,
  IconLock,
  IconStore,
  NavTabs,
  Select,
  Skeleton,
  Workspace,
} from '../../ui/v2';
import { EventsSessionProvider, useEventsScope } from './EventsScopeContext';
import { useEventsSessionQuery } from './api/queries';
import { EventsApiError, type EventsRole } from './api/types';
import { can } from './capabilities';
import {
  type EventsSection,
  RECONCILIATION_PATH,
  visibleSections,
} from './sections';

const ROLE_LABEL: Record<EventsRole, string> = {
  KITCHEN_READONLY: 'Kitchen (read-only)',
  OPERATIONS: 'Operations',
  EVENT_MANAGER: 'Event manager',
  DIRECTOR: 'Director',
  ADMIN: 'Admin',
};

const sectionActive = (pathname: string, path: string) =>
  path === '/events'
    ? pathname === path
    : pathname === path ||
      pathname.startsWith(`${path}/`) ||
      (path === '/events/pipeline' && pathname.startsWith('/events/bookings/'));

function ScopeSelectors() {
  const {
    organizations,
    organization,
    scope,
    allVenuesAllowed,
    selectOrganization,
    selectVenue,
  } = useEventsScope();
  const showOrganization = organizations.length > 1;
  // One venue is not a choice: "All venues" is withheld, and the remaining
  // option only repeats the organization name.
  const showVenue = organization.venues.length > 1;
  if (!showOrganization && !showVenue) return null;

  return (
    <>
      {showOrganization && (
        <Select
          size="sm"
          aria-label="Organization"
          className="w-44 shrink-0"
          value={organization.id}
          onChange={(event) => selectOrganization(Number(event.target.value))}
        >
          {organizations.map((org) => (
            <option key={org.id} value={org.id}>
              {org.enabled ? org.name : `${org.name} (events off)`}
            </option>
          ))}
        </Select>
      )}
      {showVenue && (
        <Select
          size="sm"
          aria-label="Venue"
          className="w-44 shrink-0"
          value={scope.venueId ?? 'org'}
          onChange={(event) =>
            selectVenue(
              event.target.value === 'org' ? null : Number(event.target.value),
            )
          }
        >
          {allVenuesAllowed && <option value="org">All venues</option>}
          {organization.venues.map((venue) => (
            <option key={venue.id} value={venue.id}>
              {venue.name}
            </option>
          ))}
        </Select>
      )}
    </>
  );
}

/**
 * The Events pages as tabs above the page title, with the organization and
 * venue selectors (only when there is more than one choice) and the role
 * at the right end. Before the session loads there are no sections yet, so
 * the bar carries only the selectors.
 */
function EventsNavigation({
  sections,
  role,
}: {
  sections: EventsSection[] | null;
  role: EventsRole | null;
}) {
  const { pathname } = useLocation();
  const { organizations, organization } = useEventsScope();
  const showScope = organizations.length > 1 || organization.venues.length > 1;
  const roleLabel = role ? (ROLE_LABEL[role] ?? role) : null;
  const trailing = (showScope || roleLabel) && (
    <>
      {showScope && <ScopeSelectors />}
      {roleLabel && (
        <span
          className="whitespace-nowrap text-v2-12 text-v2-fg-3"
          title="Your events role"
        >
          {roleLabel}
        </span>
      )}
    </>
  );

  if (sections) {
    return (
      <NavTabs
        aria-label="Events sections"
        items={sections.map((section) => ({
          to: section.path,
          label: section.label,
          active: sectionActive(pathname, section.path),
        }))}
        trailing={trailing}
      />
    );
  }
  if (!trailing) return null;
  return (
    <div className="flex min-h-11 shrink-0 items-center justify-end gap-3 border-b border-v2-line py-1.5">
      {trailing}
    </div>
  );
}

/** Header + one centred message, for states that replace the whole page. */
function GateShell({
  title = 'Events',
  children,
}: {
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <Workspace title={title}>
      <div className="v2-scroll min-h-0 flex-1 overflow-y-auto">{children}</div>
    </Workspace>
  );
}

function GateMessage({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[640px] p-4 lg:p-6">{children}</div>;
}

function PageLoading({ label }: { label: string }) {
  return (
    <div aria-busy="true" className="flex flex-col items-start p-4 lg:p-6">
      <p className="sr-only">{label}</p>
      <Skeleton className="h-4" width="28%" />
      <Skeleton className="mt-4 h-3" width="64%" />
      <Skeleton className="mt-2 h-3" width="48%" />
    </div>
  );
}

function ScopedContent() {
  const { organization, venue, scope } = useEventsScope();
  const session = useEventsSessionQuery(scope);
  const { pathname } = useLocation();

  if (session.isPending || !session.isFetchedAfterMount) {
    return (
      <>
        <EventsNavigation sections={null} role={null} />
        <GateShell>
          <PageLoading label="Loading your access…" />
        </GateShell>
      </>
    );
  }

  if (session.isError) {
    const status =
      session.error instanceof EventsApiError ? session.error.status : null;
    return (
      <>
        <EventsNavigation sections={null} role={null} />
        <GateShell>
          {status === 403 ? (
            <EmptyState
              icon={<IconLock />}
              title={`Your events role doesn't cover ${venue ? venue.name : 'all venues'}`}
              description="Choose a venue you work at from the venue selector."
            />
          ) : status === 404 ? (
            <EmptyState
              icon={<IconStore />}
              title="This organization or venue isn't available"
            />
          ) : (
            <GateMessage>
              <Callout tone="danger" title="Your events access couldn't load">
                Check your connection and try again.
                <div className="mt-2">
                  <Button size="sm" onClick={() => void session.refetch()}>
                    Retry
                  </Button>
                </div>
              </Callout>
            </GateMessage>
          )}
        </GateShell>
      </>
    );
  }

  const data = session.data;
  const onReconciliation =
    pathname === RECONCILIATION_PATH ||
    pathname.startsWith(`${RECONCILIATION_PATH}/`);

  if (data.reconciliation_only) {
    if (!can(data, 'reconciliation.read')) {
      return (
        <>
          <EventsNavigation sections={null} role={null} />
          <GateShell>
            <EmptyState
              icon={<IconLock />}
              title={`Events isn't turned on for ${organization.name}`}
              description="Inquiries, proposals and payments are paused for this organization. A director can still reconcile open payments."
              action={
                <Link
                  to="/store-info"
                  className="text-v2-12 text-v2-fg-3 hover:text-v2-fg"
                >
                  Go to Setup →
                </Link>
              }
            />
          </GateShell>
        </>
      );
    }
    if (!onReconciliation) return <Navigate to={RECONCILIATION_PATH} replace />;
  } else if (onReconciliation) {
    return <Navigate to="/events" replace />;
  }

  return (
    <EventsSessionProvider value={data}>
      <EventsNavigation sections={visibleSections(data)} role={data.role} />
      <Suspense
        fallback={
          <GateShell>
            <PageLoading label="Loading events…" />
          </GateShell>
        }
      >
        <Outlet />
      </Suspense>
    </EventsSessionProvider>
  );
}

/**
 * Nested /events layout: scope selection, session gates, section nav. Every
 * child page renders its own `PageHeader` and owns its scrolling inside the
 * `FillPage` column.
 */
export default function EventsLayout() {
  return (
    <>
      <PageMeta
        title="Events Platform | AskArthur"
        description="Manage event operations in AskArthur."
      />
      <FillPage>
        <ScopedContent />
      </FillPage>
    </>
  );
}
